<template>
  <div
    class="input-field-container radio-container"
    v-if="!disabled && clientRoleOptions"
  >
    <label> {{ $tc("Role", 2) }} </label>
    <v-combobox
      :value="selectedClientRoles"
      :items="clientRoleOptions"
      item-text="label"
      multiple
      return-object
      clearable
      class="input-field"
      @input="updateClientRolesPermissions"
    >
      <template v-slot:item="{ item, on, attrs }">
        <permissions-client-role-item
          :client-role="item"
          :permissions-selected="permissionsSelected"
          :client-roles-selected="selectedClientRoles"
          @selectClientRole="selectClientRole"
        />
      </template>
    </v-combobox>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import PermissionsClientRoleItem from "@/components/SystemManagement/Accounts/Permissions/PermissionsClientRoleItem";

export default {
  name: "PermissionsClientRole",

  components: { PermissionsClientRoleItem },

  props: {
    previousRolesAndPermissions: Object,
    permissionsSelected: Array,
    clientRolesFromParent: Array,
    disabled: Boolean,
    allPermissions: Array
  },

  data() {
    return {
      openedExpansionPanel: false,
      selectedClientRoles: [],
      clientRoleOptions: null,
      previousClientRoles: []
    };
  },

  watch: {
    selectedClientRoles(clientRoles) {
      this.$emit("update-client-roles", clientRoles);
    },

    allPermissions() {
      this.loadClientRoleOptions();
    }
  },

  methods: {
    ...mapActions(["fetchAllClientRoles", "fetchAllPermissions"]),

    /**
     * Loads a specific and filtered list of Client Roles.
     * @return {Array}
     */
    async loadClientRoleOptions() {
      try {
        let clientRoleOptions = [];
        let roleOptions = await this.fetchAllClientRoles("permissions");
        for (let role of roleOptions) {
          if (role.active && role.visible) {
            role.permissions = this.getRolesAndPermissions(role.permissions);
            clientRoleOptions.push(role);
          }
        }
        this.clientRoleOptions = clientRoleOptions;
        let clientRolesSelection = [];
        if (this.clientRolesFromParent) {
          for (let clientRole of this.clientRolesFromParent) {
            let roleOption = this.clientRoleOptions.find(
              role => role.id === clientRole.id
            );
            if (roleOption) {
              clientRolesSelection.push(roleOption);
            }
          }
        }
        this.selectedClientRoles = clientRolesSelection;
      } catch (error) {
        console.error(error);
      }
    },

    getRolesAndPermissions(clientRolePermissions) {
      let roles = [
        { name: "management", permissions: [] },
        { name: "fibercloud_web", permissions: [] },
        { name: "fibercloud_mobile", permissions: [] },
        { name: "modules", permissions: [] }
      ];
      clientRolePermissions.forEach(clientRolePermission => {
        let permission = this.allPermissions.find(
          permission => permission.id === clientRolePermission.id
        );
        if (permission && permission.roles && permission.roles[0].name) {
          let role = roles.find(role => role.name === permission.roles[0].name);
          let index = roles.indexOf(role);
          if (index > -1) {
            roles[index].permissions.push(clientRolePermission);
          }
        }
      });

      return roles;
    },

    updateClientRolesPermissions(clientRoles) {
      let removeClientRoles = this.previousClientRoles.filter(
        x => !clientRoles.includes(x)
      );

      let addClientRoles = clientRoles.filter(
        x => !this.previousClientRoles.includes(x)
      );

      if (addClientRoles.length > 0) {
        addClientRoles.forEach(addClientRole => {
          this.selectedClientRoles.push(addClientRole);
          this.addClientRolesPermissions(addClientRole);
        });
      }

      if (removeClientRoles.length > 0) {
        removeClientRoles.forEach(removeClientRole => {
          this.selectedClientRoles.splice(
            this.selectedClientRoles.indexOf(removeClientRole),
            1
          );
          this.removeClientRolesPermissions(removeClientRole, clientRoles);
        });
      }

      this.previousClientRoles = clientRoles;
    },

    selectClientRole(clientRole) {
      let index = this.selectedClientRoles.indexOf(clientRole);
      if (index < 0) {
        this.addClientRolesPermissions(clientRole);
        this.selectedClientRoles.push(clientRole);
      } else {
        this.selectedClientRoles.splice(index, 1);
        this.removeClientRolesPermissions(clientRole, this.selectedClientRoles);
      }
      this.previousClientRoles = this.selectedClientRoles;
    },

    addClientRolesPermissions(addClientRole) {
      let selectPermissions = {
        management: [],
        fibercloud_web: [],
        fibercloud_mobile: [],
        modules: []
      };

      if (addClientRole && addClientRole.permissions) {
        addClientRole.permissions.forEach(role => {
          role.permissions.forEach(permission => {
            if (
              !this.previousRolesAndPermissions[role.name].find(
                permissionId => permissionId === permission.id
              )
            ) {
              selectPermissions[role.name].push(permission.id);
              if (role.name === "fibercloud_web") {
                selectPermissions.fibercloud_mobile.push(permission.id);
              }
            }
          });
        });
      }

      this.$emit("add-select-permissions", selectPermissions);
    },

    removeClientRolesPermissions(removeClientRole, clientRoles) {
      let removeSelectionPermissions = {
        management: [],
        fibercloud_web: [],
        fibercloud_mobile: [],
        modules: []
      };

      removeClientRole.permissions.forEach(role => {
        for (let permission of role.permissions) {
          if (
            this.previousRolesAndPermissions[role.name].find(
              permissionId => permissionId === permission.id
            )
          ) {
            if (clientRoles.length === 0) {
              removeSelectionPermissions[role.name].push(permission.id);
            } else {
              let permissionsWasFoundInClientRole = false;
              for (let clientRole of clientRoles) {
                let clientRoleSelected = clientRole.permissions.find(
                  roleSelected => roleSelected.name === role.name
                );
                if (
                  clientRoleSelected.permissions.find(
                    permissionSelected =>
                      permissionSelected.id === permission.id
                  )
                ) {
                  permissionsWasFoundInClientRole = true;
                }
              }
              if (!permissionsWasFoundInClientRole) {
                removeSelectionPermissions[role.name].push(permission.id);
                if (role.name === "fibercloud_web") {
                  removeSelectionPermissions.fibercloud_mobile.push(
                    permission.id
                  );
                }
              }
            }
          }
        }
      });

      this.$emit("remove-select-permissions", removeSelectionPermissions);
    }
  }
};
</script>

<style>
.disable {
  display: none;
}
</style>

<style scoped>
.v-text-field >>> .v-input__control > .v-input__slot:after,
.v-text-field >>> .v-input__control > .v-input__slot:before {
  border-style: none !important;
}
</style>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/Permissions/PermissionsClientRole.vue